/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import { StyledProvisioningPageSubheading } from '../StyledProvisioningPageSubheading';
import { Stack } from '@mui/material';
import { BeamInput } from '../../common/formik/BeamInput';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { BeamCancelButton } from '../../common/formik/BeamCancelButton';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { useParams } from 'react-router-dom';
import { ajaxRaw } from '../../../utils/StoreUtils';
import * as yup from 'yup';
import { Client } from '../../../shared/entities/client';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentClient,
  selectCurrentClientRequestError,
  selectIsRequestingCurrentClient,
  selectRoles,
  selectRolesRequestError,
} from '../../../store';
import { BeamSelect } from '../../common/formik/BeamSelect';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import { Organization } from '../../../shared/entities/organization';
import { clearCurrentClient, requestCurrentClient } from '../../../store/clients/actions';
import { requestRoles } from '../../../store/roles/actions';
import { BeamMultiSelect } from '../../common/formik/BeamMultiSelect';
import { useAjaxFormSubmit } from '../../common/formik/useAjaxFormSubmit';

export const ClientsForm = () => {
  const dispatch = useDispatch();
  let { uuid } = useParams();

  const rolesList = useSelector(selectRoles, shallowEqual);
  const rolesRequestError = useSelector(selectRolesRequestError, shallowEqual);
  const clientToEdit = useSelector(selectCurrentClient, shallowEqual);
  const isLoadingCurrentClient = useSelector(selectIsRequestingCurrentClient, shallowEqual);
  const currentClientRequestError = useSelector(selectCurrentClientRequestError, shallowEqual);

  const [submissionError, setError] = useState<string | undefined>(undefined);
  const [orgList, setOrgList] = useState<Partial<Organization>[]>([]);
  const [initialFormState, setInitialFormState] = useState<Partial<Client>>({
    clientId: '',
    name: '',
    description: '',
    orgName: undefined,
    orgNum: undefined,
    orgId: undefined,
    roles: [],
  });
  const isEditMode = uuid !== undefined;
  const action = isEditMode ? 'Edit' : 'Add';
  const ajaxFormSubmit = useAjaxFormSubmit();

  // Fetch roles and organizations on initial load
  useEffect(() => {
    dispatch(requestRoles());
    ajaxRaw.get(`/provisioning/organizations`).subscribe({
      next: value => {
        let orgList = (value.response as Organization[]).sort((orgA, orgB) =>
          orgA.name.localeCompare(orgB.name),
        ) as Partial<Organization>[];

        setOrgList(orgList);
      },
      error: err => setError(err.message),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode) {
      if (!clientToEdit) {
        // Request existing data if we're editing a Client
        dispatch(requestCurrentClient(uuid));
      } else {
        // Update form state when clientToEdit changes
        setInitialFormState({
          ...clientToEdit,
          roles: clientToEdit.roles,
        });
      }
    }
  }, [isEditMode, uuid, clientToEdit, dispatch]);

  // Update error message if an error occurs fetching the current client or roles
  useEffect(() => {
    if (currentClientRequestError) {
      setError(currentClientRequestError);
    } else if (rolesRequestError) {
      setError(rolesRequestError);
    }
  }, [currentClientRequestError, rolesRequestError]);

  // Clear current client on unmount
  useEffect(() => {
    return () => {
      dispatch(clearCurrentClient());
    };
  }, [dispatch]);

  return (
    <>
      <StyledProvisioningPageHeading>
        {action} {!isEditMode && 'new '}client
      </StyledProvisioningPageHeading>
      <StyledProvisioningPageSubheading>
        To {action.toLowerCase()} a {!isEditMode && 'new '}client, please complete the form below.
      </StyledProvisioningPageSubheading>

      {isLoadingCurrentClient ? <ModalLoadingSpinner /> : null}

      <Formik
        enableReinitialize={true}
        initialValues={initialFormState}
        onSubmit={(values, { setSubmitting }: FormikHelpers<Partial<Client>>) => {
          setError(undefined);
          setSubmitting(true);

          let url = `/provisioning/clients`;
          let operation = ajaxRaw.postJson;
          if (isEditMode) {
            url = `/provisioning/clients/${values.clientId}`;
            operation = ajaxRaw.put;
          }

          // If the selected scope is not GLOBAL, find the corresponding orgNum and orgId
          if (values.orgName) {
            const selectedOrg = orgList?.find(org => org.name === values.orgName);
            if (selectedOrg) {
              values.orgNum = selectedOrg.orgNum;
              values.orgId = selectedOrg.id;
            }
          } else {
            // Remove org properties since an Organization was not selected
            values.orgName = undefined;
            values.orgNum = undefined;
            values.orgId = undefined;
          }

          ajaxFormSubmit(operation, url, values, setError, setSubmitting);
        }}
        validateOnChange={true}
        validationSchema={yup.object().shape({
          clientId: yup
            .string()
            .required('Please enter the client id provided by Okta.')
            .matches(/^\S*$/, 'Client id cannot contain spaces.'),
          name: yup
            .string()
            .required('Please enter a unique name for the client.')
            .max(50, 'Client name must be less than 51 characters.')
            .matches(
              /^[A-Za-z0-9_\-() ]*$/,
              'Client name can only contain letters, numbers, underscores, dashes, parentheses and spaces.',
            ),
          description: yup
            .string()
            .required('Please enter a description for the client.')
            .max(120, 'Client description must be less than 121 characters.'),
          roles: yup.array().of(yup.string()),
          orgName: yup.string().nullable(),
        })}
      >
        <Form style={{ width: '600px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {submissionError ? <ErrorMessage>{submissionError}</ErrorMessage> : null}

          <BeamInput name={'clientId'} label={'Client ID'} required={!isEditMode} disableInput={isEditMode}></BeamInput>
          <BeamInput name={'name'} label={'Client Name'} required={true}></BeamInput>
          <BeamInput name={'description'} label={'Client Description'} required={true}></BeamInput>
          <BeamSelect
            name={'orgName'}
            label={'Organization'}
            options={orgList ? orgList.map(org => org.name) : []}
            placeholder={'Select an option'}
            required={false}
          />
          <BeamMultiSelect
            name={'roles'}
            label={'Roles'}
            options={rolesList ? rolesList.map(role => role.name) : []}
            placeholder={'Select role(s)'}
            required={false}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <BeamCancelButton />
            <BeamSubmitButton />
          </Stack>
        </Form>
      </Formik>
    </>
  );
};

export default ClientsForm;
